import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const PredictionsDashboard = ({ predictionData }) => {
  const translate = useTranslate();
  const [expandedOrders, setExpandedOrders] = useState({});

  if (!predictionData || !predictionData.prediction_success) {
    return null;
  }

  const structuredData = predictionData.structured_data;
  const predictions = predictionData.predictions || [];

  if (!structuredData || predictions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {translate(
            'resources.buyers.predictions.no_data',
            'Brak danych prognoz. Kliknij "Prognozuj zamówienia" aby wygenerować prognozę.'
          )}
        </Alert>
      </Box>
    );
  }

  const metadata = structuredData.metadata || {};
  const monthNames = [
    'Styczeń',
    'Luty',
    'Marzec',
    'Kwiecień',
    'Maj',
    'Czerwiec',
    'Lipiec',
    'Sierpień',
    'Wrzesień',
    'Październik',
    'Listopad',
    'Grudzień',
  ];

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
        return 'error';
      default:
        return 'default';
    }
  };

  const getConfidenceLabel = (confidence) => {
    switch (confidence) {
      case 'high':
        return translate(
          'resources.buyers.predictions.confidence.high',
          'Wysoka'
        );
      case 'medium':
        return translate(
          'resources.buyers.predictions.confidence.medium',
          'Średnia'
        );
      case 'low':
        return translate(
          'resources.buyers.predictions.confidence.low',
          'Niska'
        );
      default:
        return confidence;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'annual':
        return translate(
          'resources.buyers.predictions.category.annual',
          'Roczne'
        );
      case 'seasonal':
        return translate(
          'resources.buyers.predictions.category.seasonal',
          'Sezonowe'
        );
      case 'periodic':
        return translate(
          'resources.buyers.predictions.category.periodic',
          'Okresowe'
        );
      default:
        return category;
    }
  };

  const getFrequencyLabel = (frequency) => {
    switch (frequency) {
      case 'yearly':
        return translate(
          'resources.buyers.predictions.frequency.yearly',
          'Rocznie'
        );
      case 'seasonal':
        return translate(
          'resources.buyers.predictions.frequency.seasonal',
          'Sezonowo'
        );
      case 'irregular':
        return translate(
          'resources.buyers.predictions.frequency.irregular',
          'Nieregularnie'
        );
      default:
        return frequency;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateTimeString;
    }
  };

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Metadata Card */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {translate(
              'resources.buyers.predictions.metadata.title',
              'Informacje o prognozie'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  'resources.buyers.predictions.metadata.generated_at',
                  'Wygenerowano'
                )}
              </Typography>
              <Typography variant="body1">
                {formatDateTime(metadata.generatedAt)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  'resources.buyers.predictions.metadata.source',
                  'Źródło'
                )}
              </Typography>
              <Typography variant="body1">{metadata.source}</Typography>
            </Grid>
            {metadata.analysisPeriod && (
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  {translate(
                    'resources.buyers.predictions.metadata.analysis_period',
                    'Okres analizy'
                  )}
                </Typography>
                <Typography variant="body1">
                  {formatDate(metadata.analysisPeriod.from)} -{' '}
                  {formatDate(metadata.analysisPeriod.to)}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  'resources.buyers.predictions.metadata.total_predictions',
                  'Liczba prognoz'
                )}
              </Typography>
              <Typography variant="h6" color="primary">
                {predictions.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Predictions List */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {translate(
          'resources.buyers.predictions.orders.title',
          'Prognozowane zamówienia okresowe'
        )}
      </Typography>

      {predictions.map((order, index) => (
        <Card key={order.id || index} sx={{ mb: 2, boxShadow: 2 }}>
          <CardContent>
            {/* Order Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {order.name}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {translate(
                      'resources.buyers.predictions.category.label',
                      'Kategoria'
                    )}
                    :{' '}
                    <Chip
                      label={getCategoryLabel(order.category)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 0.5 }}
                    />
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {translate(
                      'resources.buyers.predictions.frequency.label',
                      'Częstotliwość'
                    )}
                    :{' '}
                    <Chip
                      label={getFrequencyLabel(order.frequency)}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ ml: 0.5 }}
                    />
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {translate(
                      'resources.buyers.predictions.occurrences_label',
                      'Liczba wystąpień w historii'
                    )}
                    :{' '}
                    <Chip
                      label={`${order.occurrences} ${translate(
                        'resources.buyers.predictions.occurrences',
                        'wystąpień'
                      )}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 0.5 }}
                    />
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', ml: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {translate(
                    'resources.buyers.predictions.next_expected',
                    'Następne oczekiwane'
                  )}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {formatDate(order.nextExpected?.date)}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {translate(
                      'resources.buyers.predictions.confidence.label',
                      'Prawdopodobieństwo'
                    )}
                    :
                  </Typography>
                  <Chip
                    label={getConfidenceLabel(order.nextExpected?.confidence)}
                    size="small"
                    color={getConfidenceColor(order.nextExpected?.confidence)}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Publication and Submission Periods */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {translate(
                      'resources.buyers.predictions.publication_period',
                      'Okres publikacji'
                    )}
                  </Typography>
                </Box>
                {order.publicationPeriod?.months && (
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                    {order.publicationPeriod.months.map((month) => (
                      <Chip
                        key={month}
                        label={monthNames[month - 1]}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
                {order.publicationPeriod?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {order.publicationPeriod.description}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {translate(
                      'resources.buyers.predictions.submission_period',
                      'Okres składania ofert'
                    )}
                  </Typography>
                </Box>
                {order.submissionPeriod?.months && (
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                    {order.submissionPeriod.months.map((month) => (
                      <Chip
                        key={month}
                        label={monthNames[month - 1]}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
                {order.submissionPeriod?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {order.submissionPeriod.description}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Description */}
            {order.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {order.description}
                </Typography>
              </Box>
            )}

            {/* Historical Data - Expandable */}
            {order.historicalData && order.historicalData.length > 0 && (
              <Accordion
                expanded={expandedOrders[order.id] || false}
                onChange={() => toggleOrderExpanded(order.id)}
              >
                <AccordionSummary
                  expandIcon={
                    expandedOrders[order.id] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )
                  }
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {translate(
                        'resources.buyers.predictions.historical_data',
                        'Dane historyczne'
                      )}{' '}
                      ({order.historicalData.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {translate(
                              'resources.buyers.predictions.historical.year',
                              'Rok'
                            )}
                          </TableCell>
                          <TableCell>
                            {translate(
                              'resources.buyers.predictions.historical.publication_date',
                              'Data publikacji'
                            )}
                          </TableCell>
                          <TableCell>
                            {translate(
                              'resources.buyers.predictions.historical.submission_date',
                              'Termin składania'
                            )}
                          </TableCell>
                          <TableCell>
                            {translate(
                              'resources.buyers.predictions.historical.order_number',
                              'Nr zamówienia'
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.historicalData.map((historical, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{historical.year}</TableCell>
                            <TableCell>
                              {formatDate(historical.publicationDate)}
                            </TableCell>
                            <TableCell>
                              {formatDate(historical.submissionDate)}
                            </TableCell>
                            <TableCell>{historical.orderNumber}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default PredictionsDashboard;
